import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { saveAs } from 'file-saver';

import { PageableTableComponent } from '../../shared/controls/pageable-table/pageable-table.component';

import { InitializerService } from '../../shared/services/initializer-service';
import { ToastService } from '../../shared/toast/toast.service';
import { ErrorService } from '../../shared/services/error-service';
import { AuthService } from '../../shared/services/auth.service';
import { LoadingService } from '../../shared/services/loading.service';
import { LoadingHelper } from '../../shared/services/loading-helper';

import { HasPermissionDirective } from '../../shared/directives/has-permission.directive';
import { AreaConstants } from '../../shared/constants/area.constants';
import { PermissionConstants } from '../../shared/constants/permission.constants';

import { TenantService, DownloadService, FilteredTenantItem, PagingData } from '../../../service/proxy';

@Component({
    templateUrl: './tenant.component.html',
    styleUrls: ['./tenant.component.scss'],

    imports: [
        CommonModule,
        FormsModule,
        PageableTableComponent,
        HasPermissionDirective,
    ],

    providers: [
        TenantService,
        DownloadService,
        InitializerService,
        ToastService,
        ErrorService,
    ]
})
export class TenantComponent implements OnInit {
    public showEdit: boolean = false;

    public search: string = '';
    public paging: PagingData = new PagingData();
    public tenants: FilteredTenantItem[] = [];

    constructor(
        public authService: AuthService,
        private router: Router,
        private tenantService: TenantService,
        private initializerService: InitializerService,
        private toastService: ToastService,
        private errorService: ErrorService,
        private downloadService: DownloadService,
        private loadingService: LoadingService)
        {
            this.paging = this.initializerService.createPagingData();
        }

    async ngOnInit(): Promise<void> {
        this.initializePermissions();
    }

    initializePermissions(): void {
        this.showEdit = this.authService
            .hasPermission(AreaConstants.Tenant, PermissionConstants.Edit);
    }

    refreshGridData(): void {
        this.paging = this.initializerService.createPagingData();
        this.getTenants(this.paging);
    }

    getTenants(pagingData: PagingData = new PagingData): void {
        LoadingHelper.withLoading(
            this.tenantService.getFilteredTenants(this.search, pagingData.pageNo || 1, pagingData.pageSize || 10, pagingData.sortDirection, pagingData.sortColumn),
            this.loadingService
        ).subscribe({
            next: (data) => {
                this.paging = data.pagingData;
                this.tenants = data.results || [];
            },
            error: (error) => {
                if (error.status === 204) {
                    this.tenants = [];
                    this.paging = this.initializerService.createPagingData();
                }
                else {
                    this.errorService.handleError(error);
                }
            }
        });
    }

    getColumnMap() {
        return {
            tenantName: 'Tenant Name',
            managerEmail: 'Manager Name',
            catalog: 'Database',
            isActive: 'Active'
        };
    }

    toggleTenantStatus(event: { item: any; newValue: boolean }) {
        console.log(`Toggling tenant ${event.item.id} to ${event.newValue}`);

        const apiCall = event.newValue
            ? this.tenantService.activateTenant(event.item.id)
            : this.tenantService.deactivateTenant(event.item.id);

        LoadingHelper.withLoading(apiCall, this.loadingService).subscribe(() => {
                console.log(`Tenant ${event.item.id} status updated to ${event.newValue}`);
                event.item.isActive = event.newValue;
            },
            (error) => {
                console.error("Error updating tenant status:", error);
                alert("Failed to update tenant status.");
            }
        );
    }

    addTenant(): void {
        this.router.navigate([`/tenant/0`]);
    }

    editTenant(id: number): void {
        this.router.navigate([`/tenant/${id}`]);
    }

    downloadTenants(pagingData: PagingData = new PagingData()): void {
        LoadingHelper.withLoading(
            this.downloadService.downloadTenants(this.search, pagingData.pageNo || 1, pagingData.pageSize || 10, pagingData.sortDirection || 'Ascending', pagingData.sortColumn = ''),
            this.loadingService
        ).subscribe(response => {
            if (response.status === 200) {
                saveAs(response.data, 'Tenants.xlsx');
            }
            else {
                alert('Download failed');
            }
        });
    }
}
